/**
 * Portal Storage
 * FIX-053: Cross-Portal localStorage Token Isolation
 *
 * Factory function that creates a namespaced localStorage wrapper.
 * Each portal (APAL, HUMANDA) gets its own key prefix, preventing
 * cross-portal token contamination on localhost.
 */

// ============================================================
// Types
// ============================================================

/**
 * Namespaced localStorage wrapper for portal-specific storage.
 */
export interface PortalStorage {
  /** Read the portal's access token */
  getAccessToken(): string | null;
  /** Read the portal's refresh token */
  getRefreshToken(): string | null;
  /** Store both access and refresh tokens under namespaced keys */
  storeTokens(accessToken: string, refreshToken: string): void;
  /** Remove both access and refresh tokens */
  clearTokens(): void;
  /** Check if a non-empty access token exists */
  isAuthenticated(): boolean;

  /** Read a namespaced value */
  getItem(key: string): string | null;
  /** Write a namespaced value */
  setItem(key: string, value: string): void;
  /** Remove a namespaced value */
  removeItem(key: string): void;
}

// ============================================================
// Constants
// ============================================================

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const MIGRATION_FLAG = '__storage_migrated';

// ============================================================
// Migration
// ============================================================

/**
 * One-time migration from legacy un-prefixed keys to namespaced keys.
 *
 * - Copies `access_token` -> `{prefix}:access_token` (if target absent)
 * - Copies `refresh_token` -> `{prefix}:refresh_token` (if target absent)
 * - Sets `{prefix}:__storage_migrated` flag to prevent re-running
 * - Legacy keys are NOT deleted (other portal may still read them)
 */
function runMigration(prefix: string): void {
  const flagKey = `${prefix}:${MIGRATION_FLAG}`;

  // Already migrated -- bail out
  if (localStorage.getItem(flagKey) === 'true') {
    return;
  }

  // Copy legacy access_token if target doesn't already exist
  const legacyAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (legacyAccess && localStorage.getItem(`${prefix}:${ACCESS_TOKEN_KEY}`) === null) {
    localStorage.setItem(`${prefix}:${ACCESS_TOKEN_KEY}`, legacyAccess);
  }

  // Copy legacy refresh_token if target doesn't already exist
  const legacyRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (legacyRefresh && localStorage.getItem(`${prefix}:${REFRESH_TOKEN_KEY}`) === null) {
    localStorage.setItem(`${prefix}:${REFRESH_TOKEN_KEY}`, legacyRefresh);
  }

  // Set flag so migration never runs again for this portal
  localStorage.setItem(flagKey, 'true');
}

// ============================================================
// Factory
// ============================================================

/**
 * Create a namespaced portal storage instance.
 *
 * All keys are stored as `{portalId}:{key}` in localStorage.
 * On first invocation, migrates any legacy un-prefixed auth tokens.
 *
 * @param portalId - Portal identifier ('apal' or 'humanda')
 * @returns PortalStorage instance
 */
export function createPortalStorage(portalId: 'apal' | 'humanda'): PortalStorage {
  const prefix = portalId;

  // Run one-time migration from legacy keys
  runMigration(prefix);

  return {
    getAccessToken(): string | null {
      return localStorage.getItem(`${prefix}:${ACCESS_TOKEN_KEY}`);
    },

    getRefreshToken(): string | null {
      return localStorage.getItem(`${prefix}:${REFRESH_TOKEN_KEY}`);
    },

    storeTokens(accessToken: string, refreshToken: string): void {
      localStorage.setItem(`${prefix}:${ACCESS_TOKEN_KEY}`, accessToken);
      localStorage.setItem(`${prefix}:${REFRESH_TOKEN_KEY}`, refreshToken);
    },

    clearTokens(): void {
      localStorage.removeItem(`${prefix}:${ACCESS_TOKEN_KEY}`);
      localStorage.removeItem(`${prefix}:${REFRESH_TOKEN_KEY}`);
    },

    isAuthenticated(): boolean {
      const token = localStorage.getItem(`${prefix}:${ACCESS_TOKEN_KEY}`);
      return token !== null && token !== '';
    },

    getItem(key: string): string | null {
      return localStorage.getItem(`${prefix}:${key}`);
    },

    setItem(key: string, value: string): void {
      localStorage.setItem(`${prefix}:${key}`, value);
    },

    removeItem(key: string): void {
      localStorage.removeItem(`${prefix}:${key}`);
    },
  };
}
